/**
 * About Page
 * Personal. Direct. No corporate speak.
 */

import { Link } from 'react-router-dom';
import Button from '../components/common/Button';
import { Container, Section, Spacer } from '../components/primitives';
import { Headline, Title, BodyLarge, Body, Label } from '../components/primitives/Typography';

export default function About() {
  return (
    <main>
      {/* ========== Hero ========== */}
      <Section size="lg" className="pt-20 md:pt-32 pb-16 md:pb-24">
        <Container>
          <div className="max-w-3xl">
            <Label>About</Label>
            <Headline className="mt-4">
              Built by someone who made all the mistakes.
            </Headline>
            <BodyLarge className="mt-8">
              I returned to India in 2019 after 12 years in the Bay Area. I sold my house
              at the wrong time. Moved my 401(k) incorrectly. Put my kids in a school that
              didn't work. Anvayee exists so you don't have to repeat my mistakes.
            </BodyLarge>
          </div>
        </Container>
      </Section>

      {/* ========== The Story ========== */}
      <Section background="subtle">
        <Container narrow>
          <Label>Why this exists</Label>
          <Title className="mt-4 mb-12">The advice gap</Title>

          <div className="space-y-6">
            <Body>
              When I decided to return to India, I thought it would be straightforward.
              I had a good network. I had resources. I had time.
            </Body>
            <Body>
              What I didn't have was anyone who understood both sides. My US accountant
              didn't know Indian tax law. My Indian family didn't understand FBAR. Real
              estate agents in India had no idea what FEMA compliance meant.
            </Body>
            <Body>
              I spent months learning things the hard way. Paid penalties I didn't need to.
              Moved my kids to a school I had to pull them out of six months later.
            </Body>
            <Body>
              After talking to dozens of friends who went through similar experiences,
              I realized this wasn't a personal failure—it was a structural gap. There
              was no one helping tech professionals navigate this specific transition.
            </Body>
            <Body className="text-text-primary font-medium">
              Anvayee is an attempt to fill that gap.
            </Body>
          </div>
        </Container>
      </Section>

      {/* ========== What We Believe ========== */}
      <Section>
        <Container>
          <div className="max-w-3xl">
            <Label>What we believe</Label>
            <Title className="mt-4">Principles that guide our work</Title>
          </div>

          <div className="mt-16 grid md:grid-cols-2 gap-12 md:gap-x-16 md:gap-y-12">
            <div>
              <h3 className="text-h4 font-medium text-text-primary">Specificity over generality</h3>
              <Body className="mt-3">
                We don't give generic advice. We work with a narrow audience—senior tech
                professionals returning to India—and we go deep on their specific needs.
              </Body>
            </div>

            <div>
              <h3 className="text-h4 font-medium text-text-primary">No commission conflicts</h3>
              <Body className="mt-3">
                We don't take commissions from developers or referral fees from service
                providers. When we recommend something, it's because it's right for you.
              </Body>
            </div>

            <div>
              <h3 className="text-h4 font-medium text-text-primary">Timing matters</h3>
              <Body className="mt-3">
                Most of the value in return planning comes from starting early. Two years
                out is ideal. One year is workable. Three months is too late for tax optimization.
              </Body>
            </div>

            <div>
              <h3 className="text-h4 font-medium text-text-primary">Honest assessment</h3>
              <Body className="mt-3">
                If we can't help you, we'll say so. If your timeline doesn't work, we'll
                tell you. We'd rather lose a client than waste their time.
              </Body>
            </div>
          </div>
        </Container>
      </Section>

      {/* ========== The Name ========== */}
      <Section background="subtle">
        <Container narrow>
          <Label>The name</Label>
          <Title className="mt-4 mb-8">Anvayee</Title>
          <Body>
            "Anvayee" (अन्वयी) comes from Sanskrit. It means "one who follows" or
            "one who is connected." It reflects our approach: we walk alongside you
            through this transition, staying connected through every step of your
            journey home.
          </Body>
        </Container>
      </Section>

      {/* ========== Contact ========== */}
      <Section>
        <Container>
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <Label>Contact</Label>
              <Title className="mt-4">Get in touch</Title>
              <Body className="mt-6">
                Questions before booking? Unsure if we can help with your specific
                situation? Send us an email.
              </Body>
              <div className="mt-8 space-y-4">
                <div>
                  <p className="text-small text-text-tertiary">Email</p>
                  <a
                    href="mailto:hello@anvayee.com"
                    className="text-base text-text-primary hover:text-accent transition-colors"
                  >
                    hello@anvayee.com
                  </a>
                </div>
                <div>
                  <p className="text-small text-text-tertiary">Location</p>
                  <p className="text-base text-text-primary">Phoenix, Arizona</p>
                </div>
                <div>
                  <p className="text-small text-text-tertiary">Timezone</p>
                  <p className="text-base text-text-primary">US Mountain Time (MST)</p>
                </div>
              </div>
            </div>

            <div className="bg-surface-subtle p-8 md:p-12">
              <h3 className="text-h3 font-medium text-text-primary">
                Prefer to talk?
              </h3>
              <Body className="mt-4">
                Book a 30-minute consultation. We'll understand your situation and
                tell you honestly if we can help.
              </Body>
              <div className="mt-8">
                <Link to="/book">
                  <Button variant="primary">
                    Book a consultation
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      <Spacer size="sm" />
    </main>
  );
}
